<template>
<div class="columns is-multiline is-centered" v-if="chartOptions.regionalOption && chartOptions.localOption">
  <div class="column is-12">
    <h3 class="has-text-centered">{{ $t("profile.ABStatus.impactHF.subSectionHeader", { regionType }) }} </h3>
  </div>
  <div class="column is-12-tablet is-6-desktop spaced-left">
    <h4 class="has-text-centered">{{ $t("profile.ABStatus.impactHF.local.header") }} </h4>
    <vue-highcharts type="chart" :options="chartOptions.localOption" :redrawOnUpdate="false" :oneToOneUpdate="true" :animateOnUpdate="true" @rendered="onRender" @update="onUpdate" @destroy="onDestroy" />
    <figure>
      <div v-html='$t("profile.ABStatus.impactHF.local.caption", { sppName, regionType })' />
    </figure>
    <!-- if there is write up, show them, otherwise use automatic content -->
    <ul>
      <li class="interpretion profile auto richtext"
      v-html="$t('profile.ABStatus.impactHF.autoFigure.sectorEffect.all.' + valueDiffList.local?.mode,
      { sppName, regionType: regionType.toLowerCase(), decreaseListHFType: valueDiffList.local.decreaseStr, increaseListHFType: valueDiffList.local.increaseStr })"></li>
    </ul>
    <div v-if="['increase', 'decrease'].includes(valueDiffList?.local?.mode) ">
      <div class="interpretion profile write richtext" v-html="sppContent?.WebHFForestUnderFootprintSectorEffect" v-if="isNorth"></div>
      <div class="interpretion profile write richtext" v-html="sppContent?.WebHFPrairieUnderFootprintSectorEffect" v-if="!isNorth"></div>
    </div>
  </div>
  <div class="column is-12-tablet is-6-desktop spaced-right">
    <h4 class="has-text-centered">{{ $t("profile.ABStatus.impactHF.regional.header") }} </h4>
    <vue-highcharts type="chart" :options="chartOptions.regionalOption" :redrawOnUpdate="false" :oneToOneUpdate="true" :animateOnUpdate="true" @rendered="onRender" @update="onUpdate" @destroy="onDestroy" />
    <figure>
      <div v-html='$t("profile.ABStatus.impactHF.regional.caption", { sppName, regionType })' />
    </figure>
    <ul v-if="false"> <!-- for regional, no auto content -->
      <li class="interpretion profile auto richtext"
      v-html="$t('profile.ABStatus.impactHF.autoFigure.sectorEffect.all.' + valueDiffList.local?.mode,
      { sppName, regionType: regionType.toLowerCase(), decreaseListHFType: valueDiffList.local.decreaseStr, increaseListHFType: valueDiffList.local.increaseStr })"></li>
    </ul>
    <div>
      <div class="interpretion profile richtext" v-html="sppContent?.WebHFForestRegionalSectorEffect" v-if="isNorth"></div>
      <div class="interpretion profile richtext" v-html="sppContent?.WebHFPrairieRegionalSectorEffect" v-if="!isNorth"></div>
    </div>
  </div>
</div>
</template>

<script>
/*
check data Model
  Full: display two charts in two side by side columns: each column has a chart on top and description (common ones, and species specific) on bottom
  Forested/Prairie: display one chart on left, and description on right

  Local = local data - under
  Regional = Total Regional - data - regional
*/
import { ref, reactive, watch } from 'vue';
import { getSectorData } from '@/services/data/graph-data.js'
import Vue3Highcharts from '@/components/vue3-highcharts/index';
// import { useHFCustomIcon } from '@/components/vue3-highcharts/highchart-util'
import { useApp } from '@/composables/state/profileState';
import { credits, defaultXAxis, defaultYAxis, yTickLabelFormatter0 } from '@/config/profile-config';
import { mergeDeep } from '@/lib/common';
export default {
  name: 'hf-sector-effect-section',
  components: {
    'vue-highcharts': Vue3Highcharts
  },
  props: ['isNorth', 'isSpecies', 'hideForestry'],
  setup (props) {
    const {
      state,
      sppContent
    } = useApp();

    const chartType = ['local', 'regional']; // match data field
    const series = reactive({});
    const dotSeries = reactive({}); // show a big dot when values beyong [-100, 100]
    const categories = reactive({});
    const colors = reactive({});
    const chartOptions = reactive({});
    /* track the value between two graphs, if all increase, use lang from increase
    other wise listing which categories are increased/decrease
    Use for figure caption labelling
    */
    const valueDiffList = reactive({});
    const sppName = ref();
    sppName.value = state?.sppName;

    const regionType = props.isNorth ? 'Forested' : 'Prairie';
    const loadData = async () => {
      if (!state.tsn || props.isNorth === null) { /* these two parameters are required to get osr data */
        return;
      }

      const data = await getSectorData(state.tsn, state.year, props.isNorth, state.isSpecies); /* state.tsn */
      chartType.forEach(type => {
        valueDiffList[type] = {
          sum: 0,
          increase: [],
          decrease: []
        };
        series[type] = [];
        dotSeries[type] = [];
        categories[type] = [];
        colors[type] = [];
        data && data.values && data.values.forEach(row => {
          /* when the data's forestry model is basic, don't show the forestry in the chart
            as they will be 0
          */
          if (props.hideForestry && row.label === 'Forestry') {
            return;
          }
          const orgY = row[type];
          const scaledY = orgY > 100 ? 100 : (orgY < -100 ? -100 : orgY)
          /* make values within [-100, 100], but keep the origialY for display */

          /*

          for data labels, first make sure the labels are next to the x-axis.
          then move the labels by a fixed offset depends on the y values. e.g.
          if Y > 0, move down, if Y < 0 then move up, so the labels will be at the blank
          chart area.
          These are hardcoded in each data point.

          */
          series[type].push({
            dataLabels: {
              verticalAlign: (orgY > 0 ? 'bottom' : 'top'),
              x: 0,
              y: (orgY > 0 ? 1 : -1) * 25
            },
            orgY: orgY,
            y: scaledY
          });
          dotSeries[type].push({
            orgY,
            y: orgY > 100 ? 105 : (orgY < -100 ? -105 : null)
          });

          categories[type].push(row.label);
          colors[type].push(row.color);

          if (orgY > 0) {
            valueDiffList[type].increase.push(row.label.replaceAll('/ ', '/'));
          } else {
            valueDiffList[type].decrease.push(row.label.replaceAll('/ ', '/'));
          }
          valueDiffList[type].sum += orgY;
        });
        let diff = valueDiffList[type].increase.length - valueDiffList[type].decrease.length;

        if (categories.local.length === 4) {
          diff = diff + Math.sign(diff);
        }
        let mode = 'default' + diff;
        switch (diff) {
          case 5:
            mode = 'increase';
            break;
          case -5:
            mode = 'decrease';
            break;
          case 3:
            mode = 'majorIncrease';
            break;
          case -3:
            mode = 'majorDecrease';
            break;
          case -1:
          case 1:
          case 0:
            mode = 'mixed';
            break;
          default:
            break;
        }
        valueDiffList[type].mode = mode;
        valueDiffList[type].increaseStr = valueDiffList[type].increase.join(', ').replace(/, ([^,]*)$/, ' and $1').toLowerCase();
        valueDiffList[type].decreaseStr = valueDiffList[type].decrease.join(', ').replace(/, ([^,]*)$/, ' and $1').toLowerCase();
      });

      chartOptions.localOption = packageChartOptions(chartType[0], 'Under-footprint Sector Effects (%)');
      chartOptions.regionalOption = packageChartOptions(chartType[1], 'Regional Sector Effects (%)');
    };

    const packageChartOptions = (type, label = '') => {
      return {
        credits,
        chart: {
          type: 'column',
        },
        title: {
          text: '' // chart[type].title
        },
        xAxis: mergeDeep(defaultXAxis, {
          categories: categories[type],
          labels: {
            overflow: 'allow'
          }
        }),
        yAxis: mergeDeep(defaultYAxis, {
          min: -110,
          max: 110,
          title: {
            text: label
          },

          // gridLineWidth: 1,
          endOnTick: false,
          startOnTick: false,
          labels: yTickLabelFormatter0
          // offset: 5
        }),
        plotOptions: {
          column: {
            // grouping: true,
            stacking: 'normal', // required this tomake verticalAlign to work. (datalabels)
            dataLabels: {
              // verticalAlign: 'bottom',
              enabled: true,
              style: {
                color: '#333333'
              },
              formatter: function () {
                return (this.point.y > 0 ? '+' : '') + this.point.orgY.toFixed(2) + '%'; // + useHFCustomIcon(extenedHfCategory, 15, this.point.name)
              }
            }
          },
          line: {
            lineWidth: 0,
            dataLabels: {
              enabled: false
            }
          },
          series: {
            colorByPoint: true,
            colors: colors[type],
            groupPadding: 0.2,
          }
        },
        legend: {
          enabled: false,
          useHTML: true,
          reversed: false,
          align: 'center',
          // width: '60%',
          borderWidth: 1
          // labelFormatter: function () { return useHFCustomIcon(categories[type], 15, this.name); }
        },
        tooltip: {
          backgroundColor: '#FCFFC5',
          headerFormat: '',
          pointFormatter: function () {
            return this.category + ': ' + this.options.orgY.toFixed(2) + '%';
          }
          // pointFormat: `${point.name}: {point.y}%`
        },
        series: [{
          data: series[type]
        }, {
          type: 'line',
          data: dotSeries[type]
        }]
      }
    };

    watch(() => state.tsn, async (newVal, oldVal) => {
      if (newVal && newVal !== oldVal) {
        loadData();
      }
    });
    return {
      sppName,
      sppContent,
      loadData,
      chartOptions,
      regionType,
      valueDiffList
    }
  },
  created () {
    this.loadData();
  }
}
</script>
